import { Collection, Feature, Map } from 'ol';
import { Select } from 'ol/interaction';
import { Options, SelectEvent } from 'ol/interaction/Select';
import { Fill, Icon, Stroke, Style } from 'ol/style';

import spacePin from '@/assets/icons/space-pin-selected.svg';
import MapController from '@/shared/lib/openlayers/MapController';
import { InteractionExtraOptions } from '@/shared/lib/openlayers/interaction/AbstractInteraction';

export default class SelectInteraction extends Select {
  static getIdentifier(): string {
    return 'select';
  }

  static getExtraOpts(): InteractionExtraOptions {
    return { hasSnap: true, hasSelection: true };
  }

  static STYLE = new Style({
    fill: new Fill({ color: 'rgba(111, 63, 194,0.5)' }),
    stroke: new Stroke({ color: '#6F3FC2', width: 3 }),
    image: new Icon({ src: spacePin, width: 50, height: 50 }),
  });

  constructor(opts: Options & { selected?: Feature }, mapController?: MapController) {
    super({
      ...opts,
      style: (feature) => {
        const imageUrl = feature.get('imageUrl');
        if (!imageUrl || !mapController) {
          return SelectInteraction.STYLE;
        }

        return new Style({
          renderer: (coordinates, state) => {
            const context = state.context;

            let image = mapController.imageCache.get(imageUrl);

            if (!image) {
              image = new Image();
              image.src = imageUrl;
              mapController.imageCache.set(imageUrl, image);
              image.onload = () => {
                mapController.vectorLayer.changed();
              };
              return;
            }

            if (!image.complete) return;

            context.save();
            context.beginPath();

            (coordinates as number[][][]).forEach((ring: number[][]) => {
              ring.forEach((coord: number[], i: number) => {
                if (i === 0) context.moveTo(coord[0], coord[1]);
                else context.lineTo(coord[0], coord[1]);
              });
            });

            context.clip();

            const flatCoords = (coordinates as number[][][])[0];
            const xs = flatCoords.map((c: number[]) => c[0]);
            const ys = flatCoords.map((c: number[]) => c[1]);
            const minX = Math.min(...xs);
            const minY = Math.min(...ys);
            const maxX = Math.max(...xs);
            const maxY = Math.max(...ys);

            context.drawImage(image, minX, minY, maxX - minX, maxY - minY);

            context.restore();

            context.beginPath();
            (coordinates as number[][][]).forEach((ring: number[][]) => {
              ring.forEach((coord: number[], i: number) => {
                if (i === 0) context.moveTo(coord[0], coord[1]);
                else context.lineTo(coord[0], coord[1]);
              });
            });
            context.strokeStyle = '#6F3FC2';
            context.lineWidth = 3;
            context.stroke();
          },
        });
      },
      features: new Collection(opts.selected ? [opts.selected as Feature<any>] : []),
    });
  }

  setMap(map: Map | null) {
    super.setMap(map);
    if (!map) {
      return;
    }
    this.on('select', (event: SelectEvent) => map.dispatchEvent(event));
  }
}
